import React, { useState, useEffect } from "react";
import axios from "axios";

import EpisodeList from "../components/EpisodeList";
import Pagination from "../components/Pagination";
import CardSkeletonGrid from "../components/Skeleton";
import { handleError } from "../utils/errorHandler";
import { API_BASE } from "../utils/rickAndMorty";
import { Episode } from "../types";

const EpisodesPage: React.FC = () => {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchEpisodes = async () => {
      setIsLoading(true);
      try {
        const { data } = await axios.get(
          `${API_BASE}/episode?page=${currentPage}`
        );
        if (cancelled) return;
        setEpisodes(data.results);
        setTotalPages(data.info.pages);
        setTotalCount(data.info.count);
        setError(null);
      } catch (requestError) {
        if (cancelled) return;
        handleError(requestError);
        setError("Could not load episodes. Please try again.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchEpisodes();
    // A page switch while a request is in flight must not overwrite the newer
    // result with the older one.
    return () => {
      cancelled = true;
    };
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-white">Episodes</h1>
        <p className="mt-1 text-sm text-slate-400">
          {totalCount > 0
            ? `${totalCount} episodes across every season`
            : "Every episode of the show"}
        </p>
      </header>

      {isLoading ? (
        <CardSkeletonGrid count={8} />
      ) : error ? (
        <p className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300">
          {error}
        </p>
      ) : (
        <>
          <EpisodeList episodes={episodes} />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default EpisodesPage;
